import type { FrameworkAdapter } from './framework-adapter.js';
import { isDeprecatedFramework } from '../types/agent.js';

const adapters = new Map<string, FrameworkAdapter>();

export function registerFramework(adapter: FrameworkAdapter): void {
  adapters.set(adapter.id, adapter);
}

/** True when the framework `id` is deprecated (ENG-6919). */
export function isFrameworkDeprecated(id: string): boolean {
  return isDeprecatedFramework(id);
}

/** Operator-facing deprecation notice for a framework id. */
export function frameworkDeprecationNotice(id: string): string {
  return `[deprecated] Framework "${id}" is deprecated and no longer offered for new agents or hosts. Claude Code is the supported framework. Existing agents keep running; plan a migration to claude-code.`;
}

// ENG-6919: warn once per process per deprecated framework, and only when a
// deprecated adapter is actually resolved for use (not on module load). The
// side-effect imports in bin/agt.ts load every adapter at startup, so warning
// in registerFramework() would spam every CLI invocation and manager boot on
// healthy claude-code-only hosts. getFramework() is the single chokepoint every
// real consumer (provision, drift, manager-worker) passes through.
const warnedDeprecated = new Set<string>();

export function getFramework(id: string): FrameworkAdapter {
  const adapter = adapters.get(id);
  if (!adapter) throw new Error(`Unknown framework: "${id}". Registered: ${[...adapters.keys()].join(', ')}`);
  if (adapter.deprecated && !warnedDeprecated.has(id)) {
    warnedDeprecated.add(id);
    console.warn(frameworkDeprecationNotice(id));
  }
  return adapter;
}

export function listFrameworks(): FrameworkAdapter[] {
  return [...adapters.values()];
}
